import 'dayjs/locale/en-ca'
import 'dayjs/locale/fr'
import 'dayjs/locale/fr-ca'

import { useEffect, useMemo } from 'react'

import { useSuspenseQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { sortBy } from 'lodash-es'

import {
  type AllCentralsConfigurationHistoryEvent,
  useAllCentralsConfigurationHistory,
} from '@/model/firestore/useAllCentralsConfigurationHistory'
import { operatingRegionsQuery, regionsQuery } from '@/state/regions/regions'
import { filterActiveRegions } from '@/state/regions/regionUtils'

import { buildClosedEveningData, START_HOUR, useChartNow } from './chartUtils'
import {
  type AllCentralsConfigurationHistoryStats,
  useAllCentralsConfigurationHistoryStats,
} from './useAllCentralsConfigurationHistoryStats'

/**
 * Normalize locale string for dayjs compatibility.
 */
const normalizeDayjsLocale = (locale: string): string => {
  const lower = locale.toLowerCase()
  if (lower === 'fr') return 'fr-ca'
  if (lower === 'en') return 'en-ca'
  return lower
}

// Special value for "now" (current operating evening)
export const NOW_DATE = 'now'

export interface AvailableDate {
  date: string // YYYY-MM-DD or 'now'
  label: string
}

export interface UseAllCentralsConfigurationHistoryPageOptions {
  operatingDate: string | undefined // YYYY-MM-DD or 'now'
  locale: string
  nowLabel: string
}

export interface UseAllCentralsConfigurationHistoryPageReturn {
  availableDates: AvailableDate[]
  selectedOperatingDate: string | undefined
  stats: AllCentralsConfigurationHistoryStats
  events: AllCentralsConfigurationHistoryEvent[]
  regionNames: Record<string, string>
  eveningStartTime: Date | undefined
  eveningEndTime: Date | undefined
  isLoading: boolean
  isLive: boolean
  error: Error | undefined
}

export const useAllCentralsConfigurationHistoryPage = ({
  operatingDate,
  locale: rawLocale,
  nowLabel,
}: UseAllCentralsConfigurationHistoryPageOptions): UseAllCentralsConfigurationHistoryPageReturn => {
  const locale = normalizeDayjsLocale(rawLocale)
  const now = useChartNow()

  // Fetch all regions and operating regions
  const { data: regionsSnapshot } = useSuspenseQuery(regionsQuery())
  const { data: operatingRegionsSnapshot } = useSuspenseQuery(operatingRegionsQuery())

  // Build region name map for display
  const regionNames = useMemo((): Record<string, string> => {
    const map: Record<string, string> = {}
    for (const region of regionsSnapshot.data) {
      map[region._id] = region.name ?? 'Unknown'
    }
    return map
  }, [regionsSnapshot.data])

  // Check if any region is currently operating
  const hasOperatingRegions = useMemo(
    () => filterActiveRegions(operatingRegionsSnapshot.data).length > 0,
    [operatingRegionsSnapshot.data]
  )

  // Build closed evening data across all regions (union of all dates)
  const { closedDates } = useMemo(
    () => buildClosedEveningData(regionsSnapshot.data, locale),
    [regionsSnapshot.data, locale]
  )

  // Combine "now" option with closed dates
  const availableDates = useMemo((): AvailableDate[] => {
    const result: AvailableDate[] = []
    if (hasOperatingRegions) {
      result.push({ date: NOW_DATE, label: nowLabel })
    }
    result.push(...closedDates)
    return result
  }, [hasOperatingRegions, closedDates, nowLabel])

  // Resolve the actual operating date to query
  const resolvedOperatingDate = useMemo((): string | undefined => {
    if (!operatingDate) return undefined
    if (operatingDate === NOW_DATE) {
      // For "now", find the current operating date from any active region
      const activeRegions = filterActiveRegions(operatingRegionsSnapshot.data)
      if (activeRegions.length > 0) {
        return activeRegions[0].currentOperatingDate?.date
      }
      return undefined
    }
    return operatingDate
  }, [operatingDate, operatingRegionsSnapshot.data])

  // Determine if we're viewing a live evening
  const isLive = operatingDate === NOW_DATE

  // Fetch configuration history across all centrals
  const { events, isLoading, error } = useAllCentralsConfigurationHistory({
    operatingDate: resolvedOperatingDate,
    isLive,
  })

  // Sort events by timestamp
  const sortedEvents = useMemo(() => sortBy(events, (e) => e.timestamp.getTime()), [events])

  // Calculate evening start and end times
  const { eveningStartTime, eveningEndTime } = useMemo(() => {
    if (!resolvedOperatingDate) {
      return { eveningStartTime: undefined, eveningEndTime: undefined }
    }

    // Find the earliest event timestamp (when any evening was opened)
    const earliestEventTime = sortedEvents.length > 0 ? sortedEvents[0].timestamp : null

    const startTime: Date =
      earliestEventTime ?? dayjs(resolvedOperatingDate).hour(START_HOUR).minute(0).second(0).toDate()

    const endTime = isLive
      ? new Date(Math.max(now.getTime(), startTime.getTime()))
      : dayjs(resolvedOperatingDate).add(1, 'day').hour(4).minute(0).second(0).toDate()

    return { eveningStartTime: startTime, eveningEndTime: endTime }
  }, [resolvedOperatingDate, isLive, now, sortedEvents])

  // Calculate aggregate stats
  const stats = useAllCentralsConfigurationHistoryStats(sortedEvents, regionNames)

  // Log errors to console (especially useful for Firestore index creation links)
  useEffect(() => {
    if (error) {
      console.error('[AllCentralsConfigurationHistory] Query error:', error)
    }
  }, [error])

  return {
    availableDates,
    selectedOperatingDate: operatingDate,
    stats,
    events: sortedEvents,
    regionNames,
    eveningStartTime,
    eveningEndTime,
    isLoading,
    isLive,
    error: error ?? undefined,
  }
}
